import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    booleanAttribute,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
} from "@angular/core";
import { rxResource, toObservable } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { DocumentService } from "@malirang/api-client";
import {
    AdvancedCounselingTemplate,
    ChangeContractTemplate,
    ChildAdolescentServiceRequestTemplate,
    ChildServiceAgreementTemplate,
    DefaultCounselingTemplate,
    DevelopmentRehabilitationServicePlanTemplate,
    DevelopmentRehabilitationServiceRequestTemplate,
    DevelopmentServiceAgreementTemplate,
    EvaluationReportTemplate,
    InterimReportTemplate,
    PrivateInfoAgreementTemplate,
    ReasonStatementTemplate,
    ServiceRecordTemplate,
    ServiceScheduleTemplate,
    TermTreatmentPlanTemplate,
} from "@malirang/app-share";
import {
    DocumentEtcType,
    DocumentInServiceType,
    DocumentPreServiceType,
} from "@malirang/common";
import {
    AlertService,
    Button,
    Icon,
    ModalService,
    ToastService,
} from "@malirang/front-share";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import * as PinchZoom from "pinch-zoom-js";
import dayjs from "dayjs";
import { of } from "rxjs";
import { environment } from "../../../../../../env/env";
import { CenterRoleStore } from "../../../../../../stores/center/role.store";
import { injectQueryParams } from "ngxtension/inject-query-params";
import {
    DocumentShareModal,
    DocumentShareModalProps,
} from "../../../modals/document-share/document-share.modal";
import { DocumentStore } from "../../../store/document.store";

@Component({
    selector: "app-fix-document-content",
    templateUrl: "./fix-document-content.component.html",
    styleUrls: ["./fix-document-content.component.css"],
    imports: [
        CommonModule,
        Button,
        Icon,

        // 상담 전 문서
        DefaultCounselingTemplate,
        EvaluationReportTemplate,
        DevelopmentRehabilitationServiceRequestTemplate,
        ChildAdolescentServiceRequestTemplate,

        // 진행중 (공통)
        TermTreatmentPlanTemplate,
        InterimReportTemplate,
        AdvancedCounselingTemplate,
        DevelopmentServiceAgreementTemplate,
        ChildServiceAgreementTemplate,
        PrivateInfoAgreementTemplate,
        ServiceRecordTemplate,
        ServiceScheduleTemplate,
        DevelopmentRehabilitationServicePlanTemplate,

        // 기타
        ChangeContractTemplate,
        ReasonStatementTemplate,
    ],
})
export class FixDocumentContentComponent {
    readonly documentService = inject(DocumentService);
    readonly documentStore = inject(DocumentStore);
    readonly router = inject(Router);
    readonly centerRoleStore = inject(CenterRoleStore);
    readonly modalService = inject(ModalService);
    readonly alert = inject(AlertService);
    readonly toast = inject(ToastService);

    documentSection = viewChild<ElementRef<HTMLElement>>("documentSection");
    documentSection$ = toObservable(this.documentSection);

    readRole = this.documentStore.readRole;
    editRole = this.documentStore.editRole;
    deleteRole = this.documentStore.deleteRole;

    documentId = input.required<string>();
    printMode = input<boolean, string>(false, { transform: booleanAttribute });
    ticketId = computed(() => this.documentStore.currentTicket()?.id);

    // 신규 문서 여부 확인
    isNewDocument = computed(() => this.documentId() === "new");
    type = injectQueryParams("type");
    phase = injectQueryParams("phase");

    sortType = signal<"latest" | "oldest">("latest");

    $document = rxResource({
        stream: ({ params }) =>
            params.id && params.id !== "new" && params.id !== "form"
                ? this.documentService.documentControllerFindById({
                      id: params.id || "",
                  })
                : of(null),
        params: () => ({ id: this.documentId() }),
    });
    document = computed(() =>
        this.$document.hasValue() ? this.$document.value() : null,
    );
    document$ = toObservable(this.document);

    preType = DocumentPreServiceType;
    inType = DocumentInServiceType;
    etcType = DocumentEtcType;

    isPreDocument = computed(() => this.document()?.phase === "PRE_SERVICE");
    isInDocument = computed(() => this.document()?.phase === "IN_SERVICE");
    isEtcDocument = computed(() => this.document()?.phase === "ETC");

    useSideMenu = computed(
        () =>
            this.document()?.inServiceType === "서비스제공기록지" ||
            this.document()?.inServiceType === "서비스일정표",
    );

    sideMenuItems = computed(() => {
        const fixDocuments = this.documentStore.fixDocuments();
        let filtered: any[] = [];

        if (this.document()?.inServiceType === "서비스제공기록지") {
            filtered = fixDocuments.filter(
                (d) => d.inServiceType === "서비스제공기록지",
            );
        } else if (this.document()?.inServiceType === "서비스일정표") {
            filtered = fixDocuments.filter(
                (d) => d.inServiceType === "서비스일정표",
            );
        }

        return filtered.sort((a, b) => {
            const dateA = this.getDocumentDate(a);
            const dateB = this.getDocumentDate(b);
            const diff = dayjs(dateA).diff(dayjs(dateB));
            return this.sortType() === "latest" ? -diff : diff;
        });
    });

    private getDocumentDate(doc: any): string | Date {
        const data = doc.data as any;
        if (doc.inServiceType === "서비스일정표") {
            return dayjs()
                .year(data.year)
                .month(data.month - 1)
                .format("YYYY-MM");
        }
        return data.month || doc.updatedAt;
    }

    name = computed(() => {
        const isPre = this.isPreDocument();
        if (isPre) {
            return DocumentPreServiceType[this.document()?.preServiceType!];
        }

        const isEtc = this.isEtcDocument();
        if (isEtc) {
            return DocumentEtcType[this.document()?.etcType!];
        }

        return DocumentInServiceType[this.document()?.inServiceType!];
    });

    enableAi = computed(() => {
        if (this.isPreDocument()) {
            return (
                this.document()?.preServiceType !== "기본상담지" &&
                this.document()?.preServiceType !== "심화상담지"
            );
        }

        if (this.isInDocument()) {
            return (
                this.document()?.inServiceType === "장단기계획서" ||
                this.document()?.inServiceType === "중간보고서" ||
                this.document()?.inServiceType === "종결보고서"
            );
        }

        if (this.isEtcDocument()) {
            return false;
        }

        return true;
    });

    isLoading = signal(false);
    values = computed<any>(() => this.document()?.data || {});

    constructor() {
        afterNextRender(() => {
            if (window.innerWidth < 768) {
                this.documentSection$.subscribe({
                    next: (section) => {
                        if (section!.nativeElement) {
                            const pz = new PinchZoom.default(
                                section!.nativeElement,
                                {
                                    draggableUnzoomed: false,
                                    minZoom: 1,
                                },
                            );
                        }
                    },
                });
            }
        });
    }

    handleSideMenuClick(id: string) {
        this.router.navigate(["/document/document-box/fix", id], {
            queryParamsHandling: "merge",
        });
    }

    handleAdd() {
        this.router.navigate(["/document/document-box/fix/form"], {
            queryParamsHandling: "merge",
            queryParams: {
                documentId: "",
                type: this.document()?.inServiceType,
                phase: this.document()?.phase,
                ticketId: this.ticketId(),
            },
        });
    }

    handleDelete() {
        this.alert
            .open({
                title: "문서 삭제",
                content: `문서를 삭제하시겠습니까?
      문서 삭제 후에는 복구할 수 없습니다.`,
                type: "danger",
                buttons: {
                    confirm: {
                        color: "red",
                    },
                },
                checkbox: {
                    text: "데이터 삭제 후 복구할 수 없음을 확인하였습니다.",
                },
            })
            .closed.subscribe({
                next: (res) => {
                    if (res?.action === "confirm") {
                        this.documentService
                            .documentControllerDeleteDocument({
                                id: this.documentId()!,
                            })
                            .subscribe({
                                next: () => {
                                    this.documentStore.reload$.next();
                                    this.documentStore.reloadCurrentTicket();
                                    // 문서 목록도 모두 reload
                                    this.documentStore.reloadPreDocuments();
                                    this.documentStore.reloadFixDocuments();
                                    this.documentStore.reloadEtcDocuments();
                                    this.toast.success(
                                        "문서가 삭제되었습니다.",
                                    );
                                    this.router.navigate(
                                        ["/document/document-box"],
                                        {
                                            queryParamsHandling: "merge",
                                            queryParams: {
                                                ticketId: this.ticketId(),
                                                documentId: null,
                                            },
                                        },
                                    );
                                },
                            });
                    }
                },
            });
    }

    handleEdit() {
        // 신규 문서인 경우
        if (this.isNewDocument()) {
            const phase = this.phase();
            const type = this.type();

            if (phase === "PRE_SERVICE" || !phase) {
                // pre-document form으로 이동
                this.router.navigate(["/document/document-box/pre/form"], {
                    queryParams: { type },
                    queryParamsHandling: "merge",
                });
            } else if (this.isEtcDocument()) {
                // etc-document form으로 이동
                this.router.navigate(
                    [
                        "/document/document-box/etc",
                        this.document()?.id,
                        "/form",
                    ],
                    {
                        queryParams: {
                            type,
                            phase,
                            documentId: undefined,
                        },
                        queryParamsHandling: "merge",
                    },
                );
            } else {
                // fix-document form으로 이동
                this.router.navigate(["/document/document-box/fix/form"], {
                    queryParams: {
                        type,
                        phase,
                        documentId: undefined,
                    },
                    queryParamsHandling: "merge",
                });
            }
            return;
        }

        // 기존 pre-document인 경우
        if (this.isPreDocument()) {
            this.router.navigate(["/document/document-box/pre/form"], {
                queryParams: {
                    type: this.document()?.preServiceType,
                },
                queryParamsHandling: "merge",
            });
            return;
        }

        // 기존 fix-document/etc-document인 경우
        this.router.navigate(["/document/document-box/fix/form"], {
            queryParams: {
                documentId: this.documentId(),
                phase: this.document()?.phase,
                type:
                    this.document()?.inServiceType || this.document()?.etcType,
            },
            queryParamsHandling: "merge",
        });
    }

    async handleDownload() {
        this.isLoading.set(true);

        const zip = new JSZip();

        const doc = this.document()!;
        const documentId = doc.id;
        const url = `${window.location.origin}/document-box/pdf?documentId=${documentId}`;

        const fileName = () => {
            if (this.isPreDocument()) {
                return `${DocumentPreServiceType[this.document()?.preServiceType!]}`;
            }

            if (this.isInDocument()) {
                return `${DocumentInServiceType[this.document()?.inServiceType!]}`;
            }

            if (this.isEtcDocument()) {
                return `${DocumentEtcType[this.document()?.etcType!]}`;
            }

            return `${doc.template.name}`;
        };

        try {
            const res = await fetch(
                `${environment.baseUrl}/api/pdf?url=${encodeURIComponent(url)}`,
            );
            const blob = await res.blob();
            zip.file(`${fileName()}.pdf`, blob);
            //
        } catch (error) {
            this.isLoading.set(false);
        }

        // zip 파일 생성
        const content = await zip.generateAsync({ type: "blob" });

        // 다운로드 실행
        saveAs(content, `${fileName()}.zip`);
        this.isLoading.set(false);
    }

    handlePrint() {
        // popup
        window.open(
            `${window.location.origin}/print-document/${this.documentId()}`,
            "_blank",
            `popup,width=4000,height=4000,top=0,left=0`,
        );

        console.log(
            `${window.location.origin}/print-document/${this.documentId()}`,
        );
    }

    handleShare() {
        const modal = this.modalService.create<DocumentShareModalProps>(
            DocumentShareModal,
            {
                componentProps: {
                    documents: [this.document()!],
                },
            },
        );

        modal.closed.subscribe({
            next: (res) => {
                if (res) {
                    this.$document.reload();
                }
            },
        });
    }
}
